import React, { useState } from "react";
import {
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  Text,
  Box,
  VStack,
  Textarea,
  TableContainer,
  Table,
  UnorderedList,
  ListItem,
  useToken,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Divider,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import {
  MdEmail,
  MdPhone,
  MdMessage,
  MdUploadFile,
  MdDownload,
  MdSearch,
  MdAdd,
  MdOutlineEmail,
} from "react-icons/md";
import axiosInstance from "../../Services/axiosInstance";
import TableHead from "../Campaigns/components/TableHead";
import SuppressionListTable from "./SuppressionListTable";
import { useToast } from "@chakra-ui/react";
import { useQuery } from "react-query";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import CsvDropzone from "./CsvDropzone";
import Swal from "sweetalert2";
import hexToRgba from "hex-to-rgba";
import { getSegmentForSuppersionList, addExclusionList } from "../../Services/api";
import Pagination from "../../components/Pagination";
import * as _ from "lodash";
import { BsWhatsapp } from "react-icons/bs";

const SuppressionList = () => {
  const [channel, setChannel] = useState("email");
  const [totalCount, setTotalCount] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [suppressionList, setSuppressionList] = useState([]);
  const [mainColor] = useToken("colors", ["primary.main"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [exclusionList, setExclusionList] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const toast = useToast();
  const orgDetails = JSON.parse(localStorage.getItem("selectedOrg") || "{}");
  const orgToken = orgDetails.token;

  const channels = [
    { 
      key: "email", 
      label: "Email", 
      icon: MdOutlineEmail,
      color: "blue.500",
      bg: "blue.50"
    },
    { 
      key: "sms", 
      label: "SMS", 
      icon: MdMessage,
      color: "green.500",
      bg: "green.50"
    },
    { 
      key: "whatsapp", 
      label: "WhatsApp", 
      icon: BsWhatsapp,
      color: "green.600",
      bg: "green.50"
    },
  ];

  const fetchSuppressionListQuery = useQuery(
    ["suppressionList", channel, currentPage, searchQuery],
    async () => {
      const url = `/suppression/getlist/${channel}?page=${currentPage}&perpage=${pageSize}`;
      const response = await axiosInstance.get(url);
      const filteredData = response.data.items.filter((item) => {
        const query = searchQuery.toLowerCase();
        return (
          item?.email?.toLowerCase().includes(query) ||
          item?.phone?.toLowerCase().includes(query)
        );
      });
      setSuppressionList(filteredData);
      setTotalCount(response.data.count);
      return filteredData;
    },
    {
      staleTime: 0,
      retry: 1,
      enabled: !!channel && !!orgToken,
    }
  );

  const fetchCustomerList = useQuery(
    ["customerList", orgToken],
    async () => {
      const response = await getSegmentForSuppersionList(orgToken, {});
      setExclusionList(response.data);
      return response.data;
    },
    {
      enabled: !!orgToken,
    }
  );

  const handleChannelChange = (selectedChannel) => {
    setChannel(selectedChannel);
    setInputValue("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleCsvChange = (file) => {
    setCsvFile(file);
    handleCsvUpload(file);
  };

  const handleSubmit = async () => {
    const data = channel === "sms" || channel === "whatsapp" ? { phone: inputValue } : { email: inputValue };
    try {
      const response = await axiosInstance.post(
        `/suppression/rest/v1/${channel === "sms" || channel === "whatsapp" ? "sms" : "email"}/${btoa(inputValue)}`,
        data
      );
      if (response?.data) {
        fetchSuppressionListQuery.refetch();
        setInputValue("");
        toast({
          title: "Contact added.",
          description: "The contact has been added to the suppression list.",
          status: "success",
          duration: 1000,
          isClosable: true,
          position: "bottom-right",
        });
      }
    } catch (error) {
      console.error("Error adding to suppression list:", error);
    }
  };

  const handleExclusionChange = (e) => {
    const selectedId = parseInt(e.target.value);
    const selected = exclusionList?.data?.find((option) => option?.id === selectedId);
    if (selected) {
      setSegmentId(selected.id);
    }
  };

  const handleExclusionSubmit = async () => {
    try {
      const data = await addExclusionList(segmentId);
      toast({
        title: "Exclusion added.",
        description: "The segment has been added to the exclusion list.",
        status: "success",
        duration: 1000,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      console.error("Error adding to exclusion list:", error);
    }
  };

  const deleteFromSuppression = async (contact, channel) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to remove this contact: ${contact} from suppression?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const resp = await axiosInstance.post(
            `/suppression/rest/v1/${channel}/${contact}`
          );
          if (resp) {
            fetchSuppressionListQuery.refetch();
            Swal.fire("Deleted!", "The contact has been removed.", "success");
          }
        } catch (error) {
          Swal.fire("Error!", "There was an error removing the contact.", "error");
        }
      }
    });
  };

  const convertJsonToCsv = (json) => {
    if (!json || json.length === 0) return "";
    const array = [Object.keys(json[0])].concat(json.map((obj) => Object.values(obj)));
    return array.map((row) => row.join(",")).join("\n");
  };

  const downloadCsv = (csvData, fileName) => {
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = (jsonData, fileName) => {
    if (!jsonData || jsonData.length === 0) {
      toast({
        title: "No data to download.",
        description: "There are no contacts in the suppression list to export.",
        status: "warning",
        duration: 1000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    const csv = convertJsonToCsv(jsonData);
    downloadCsv(csv, fileName);
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosInstance.post(
        `/rest/v1/uploadfile/wzbroadcast/${channel}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data) {
        const res = await axiosInstance.post(
          `/suppression/rest/v1/${channel}?mediaUrl=${btoa(response.data.url)}`,
          {}
        );
        if (res) {
          fetchSuppressionListQuery.refetch();
          toast({
            title: "CSV file uploaded.",
            description: "The CSV file has been successfully uploaded and contacts added to the suppression list.",
            status: "success",
            duration: 1000,
            isClosable: true,
            position: "bottom-right",
          });
        }
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      toast({
        title: "Upload failed.",
        description: "There was an error uploading the CSV file.",
        status: "error",
        duration: 1000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  const currentChannel = channels.find(c => c.key === channel);

  return (
    <Box p={{ base: 4, md: 6 }} maxW="8xl" mx="auto">
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg" color="gray.800">
          Suppression List
        </Heading>
        <Badge 
          colorScheme="purple" 
          fontSize="sm" 
          px={3} 
          py={1}
          variant="solid"
        >
          {totalCount} contacts
        </Badge>
      </Flex>

      {/* Sleek Channel Tabs */}
      <Card mb={8} variant="elevated">
        <CardBody py={6}>
          <Text fontSize="lg" fontWeight="600" mb={4} color="gray.700">
            Select Channel
          </Text>
          <HStack spacing={3}>
            {channels.map(({ key, label, icon: Icon, color, bg }) => (
              <Tooltip key={key} label={label} placement="top">
                <Box
                  as="button"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  p={3}
                  minW="100px"
                  borderRadius="lg"
                  bg={channel === key ? color : bg}
                  borderWidth={channel === key ? "2px" : "1px"}
                  borderColor={channel === key ? color : "gray.200"}
                  transition="all 0.2s ease"
                  cursor="pointer"
                  shadow={channel === key ? "md" : "sm"}
                  _hover={{
                    transform: "translateY(-1px)",
                    shadow: channel === key ? "lg" : "md",
                  }}
                  onClick={() => handleChannelChange(key)}
                  position="relative"
                  overflow="hidden"
                  _focus={{ outline: "none", boxShadow: `0 0 0 3px ${hexToRgba(color, 0.3)}` }}
                >
                  <Icon size="20px" color={channel === key ? "black" : color} mb={1} />
                  <Text 
                    fontSize="sm" 
                    fontWeight="600" 
                    color={channel === key ? "whiteAlpha.900" : "gray.700"}
                    textAlign="center"
                  >
                    {label}
                  </Text>
                  {channel === key && (
                    <Box
                      position="absolute"
                      top="2px"
                      right="2px"
                      w="6px"
                      h="6px"
                      bg="white"
                      borderRadius="full"
                      boxShadow="0 0 0 2px white"
                    />
                  )}
                </Box>
              </Tooltip>
            ))}
          </HStack>
        </CardBody>
      </Card>

      {/* Rest of the component remains exactly the same */}
      {channel && (
        <VStack spacing={6} w="100%">
          {/* Exclusion List Card */}
          <Card w="full" variant="outline">
            <CardHeader pb={2}>
              <Flex align="center" gap={3}>
                <MdAdd size="20px" color="gray.600" />
                <Heading size="md">Add Exclusion List</Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <Select
                placeholder="Select customer segment for exclusion"
                bg="gray.50"
                borderRadius="lg"
                mb={4}
                onChange={handleExclusionChange}
                size="lg"
              >
                {exclusionList?.data?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.aggregates?.label || type.name}
                  </option>
                ))}
              </Select>
              <PrimaryButton
                w="full"
                label="Add to Exclusion List"
                leftIcon={<MdAdd />}
                onClick={handleExclusionSubmit}
              />
            </CardBody>
          </Card>

          {/* Manual Entry Card */}
          <Card w="full" variant="elevated">
            <CardHeader pb={2}>
              <Flex align="center" gap={3}>
                <MdAdd size="20px" color="blue.500" />
                <Heading size="md">
                  Add {currentChannel.label} Contacts
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <Textarea
                bg="gray.50"
                borderRadius="lg"
                size="lg"
                minH="120px"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={`Enter ${currentChannel.label === 'WhatsApp' || currentChannel.label === 'SMS' ? 'phone numbers' : 'emails'} (one per line)`}
              />
              <PrimaryButton
                mt={4}
                w="full"
                label={`Add ${totalCount === 0 ? 'First' : totalCount} Contact${totalCount !== 1 ? 's' : ''}`}
                leftIcon={<MdAdd />}
                onClick={handleSubmit}
              />
            </CardBody>
          </Card>

          {/* Bulk Upload Section */}
          <Divider />
          <Card w="full" direction={{ base: "column", lg: "row" }} variant="outline">
            <CardBody flex={1}>
              <Flex align="center" gap={3} mb={4}>
                <MdUploadFile size="20px" color="gray.600" />
                <Text fontSize="lg" fontWeight="600">Bulk Upload</Text>
              </Flex>
              <CsvDropzone handleCsvChange={handleCsvChange} />
            </CardBody>
            <Divider orientation="vertical" display={{ lg: "block", base: "none" }} />
            <CardBody p={{ base: 6, lg: 8 }}>
              <Heading size="sm" mb={4} color="gray.700">📋 Instructions</Heading>
              <UnorderedList spacing={3} fontSize="sm">
                <ListItem>Enter phone/email IDs on new lines for manual entry</ListItem>
                <ListItem>Upload <strong>.CSV</strong> files for bulk import</ListItem>
                <ListItem>Ensure valid {currentChannel.label.toLowerCase()} formats</ListItem>
                <ListItem>Duplicates will be automatically skipped</ListItem>
              </UnorderedList>
            </CardBody>
          </Card>

          {/* Download & Table Section */}
          <Card w="full" variant="elevated">
            <CardHeader p={6}>
              <Flex justify="space-between" align="center" w="full">
                <Flex align="center" gap={3}>
                  <MdDownload size="20px" />
                  <Text fontSize="lg" fontWeight="600">
                    Suppression List ({suppressionList.length} shown)
                  </Text>
                </Flex>
                <PrimaryButton
                  leftIcon={<MdDownload />}
                  label="Download CSV"
                  size="sm"
                  onClick={() =>
                    handleDownload(
                      suppressionList,
                      `suppression_${channel}_report_${orgDetails.domain}.csv`
                    )
                  }
                />
              </Flex>
            </CardHeader>
            <CardBody>
              <Input
                placeholder="🔍 Search contacts..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                mb={6}
                size="lg"
                pl={12}
                borderRadius="lg"
                bg="gray.50"
              />
              <TableContainer>
                <Table size="sm" variant="striped" colorScheme="gray">
                  <TableHead
                    headersProps={
                      channel === "email"
                        ? ["Sr. No.", "Email", "Reason", "Date", "Action"]
                        : ["Sr. No.", "Phone", "Reason", "Date", "Action"]
                    }
                  />
                  {suppressionList.length === 0 ? (
                    <Box py={12} textAlign="center">
                      <Text fontSize="lg" color="gray.500" mb={2}>
                        No contacts found
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        {searchQuery ? 'Try adjusting your search terms' : `Start by adding your first ${currentChannel.label.toLowerCase()} contacts`}
                      </Text>
                    </Box>
                  ) : (
                    <SuppressionListTable
                      deleteFromSuppression={deleteFromSuppression}
                      list={suppressionList}
                      channel={channel}
                    />
                  )}
                </Table>
                {suppressionList.length > 0 && (
                  <Pagination
                    totalCount={totalCount}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    isDisabled={fetchSuppressionListQuery.isLoading}
                  />
                )}
              </TableContainer>
            </CardBody>
          </Card>
        </VStack>
      )}

      {/* Loading state for no channel selected */}
      {!channel && (
        <Card w="full" textAlign="center" py={20} variant="outline">
          <Heading size="lg" color="gray.500" mb={4}>
            Select a channel to get started
          </Heading>
          <Text color="gray.400" fontSize="lg">
            Choose Email, SMS, or WhatsApp to manage suppression lists
          </Text>
        </Card>
      )}
    </Box>
  );
};

export default SuppressionList;
